import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import PageHeader from '@/components/common/PageHeader';
import Reveal from '@/components/common/Reveal';
import { industries } from '@/data/projects';
import { buttonVariants, Button } from '@/components/ui/button';

const notes = [
  {
    name: 'Dermatology',
    kicker: 'Where the site is the pre-consultation.',
    body:
      'Skin patients research quietly. A dermatology site has to answer questions the phone call won\'t: pigmentation vs. melasma, laser type, downtime, cost bands. We build these into calm, editorial pages so the consultation begins before it starts.',
    bullets: ['Treatment glossaries', 'Before/after with dignity', 'Fee ranges, honestly'],
  },
  {
    name: 'Dental',
    kicker: 'Trust is built in the first fold.',
    body:
      'Dental patients decide fast — and rarely on price alone. Photography of the practice, clarity around the practitioner, and a booking flow that doesn\'t feel like a form are the three things we obsess over.',
    bullets: ['Practitioner-led narrative', 'Warm chair-side photography', 'One-tap WhatsApp booking'],
  },
  {
    name: 'Aesthetic & Cosmetic',
    kicker: 'Luxury without loudness.',
    body:
      'The aesthetic category is drowning in gradients and gold. We answer with restraint — serif type, quiet motion, editorial imagery — the tone of a boutique, not a billboard.',
    bullets: ['Editorial art direction', 'Discreet treatment menus', 'Consultation-first CTAs'],
  },
  {
    name: 'Hair Clinics',
    kicker: 'A category that needs reassurance, not sales.',
    body:
      'Hair loss is personal. Copy that respects that — plus visible practitioner credentials, real timelines and calm case studies — outperforms every trending landing-page template we\'ve tested against.',
    bullets: ['Timeline-based case studies', 'Practitioner credentials up top', 'FAQ-led structure'],
  },
  {
    name: 'Physicians & Multi-speciality',
    kicker: 'A digital second opinion.',
    body:
      'For general physicians and multi-doctor clinics, we design a site that behaves like a second waiting room: a clear list of specialities, transparent hours, and no jargon.',
    bullets: ['Doctor rota clarity', 'Speciality index', 'Location-first hero'],
  },
  {
    name: 'Beyond healthcare',
    kicker: 'Hospitality, nonprofits and event studios.',
    body:
      'Half a dozen sites outside medicine, kept on our shelf because they share the same DNA: quiet type, generous space, and a brief that trusted the studio to think.',
    bullets: ['Restaurants', 'Nonprofits', 'Event management'],
  },
];

export default function IndustriesPage() {
  return (
    <motion.main
      id="main-content"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Helmet>
        <title>Industries | TechsFlow</title>
      </Helmet>
      <PageHeader
          eyebrow="Index · 02 · Industries"
          title="Six categories,"
          italicTail="one bedside manner."
          lede="We say healthcare-first for a reason: it's where our details matter most. But the way we work travels — to restaurants, nonprofits, and any brand that values quiet more than loud."
      >
        <div className="mt-6">
          <Link
              to="/contact"
              className={buttonVariants({ variant: 'editorial' })}
          >
            Talk to us about your category
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </PageHeader>

      <section className="py-16 md:py-24">
        <div className="mx-auto container-editorial space-y-20 md:space-y-28">
          {notes.map((n, i) => (
            <Reveal key={n.name}>
              <article className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
                <div className="lg:col-span-4">
                  <div className="label-eyebrow mb-4">Chapter · {String(i + 1).padStart(2, '0')}</div>
                  <h3 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.98] tracking-tight text-balance">
                    {n.name}
                  </h3>
                </div>
                <div className="lg:col-span-8">
                  <p className="italic-serif text-2xl md:text-3xl text-foreground leading-snug mb-6 max-w-2xl">
                    {n.kicker}
                  </p>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mb-8">
                    {n.body}
                  </p>
                  <ul className="flex flex-wrap gap-2">
                    {n.bullets.map(b => (
                      <li key={b}
                          className="px-3.5 py-1.5 rounded-full border border-border text-xs font-mono uppercase tracking-widest text-muted-foreground">
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            </Reveal>
          ))}
          <Link to="/contact" className={buttonVariants({ variant: 'editorial' })}>
            Talk to us about your category <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/*<section className="py-20 md:py-28 border-t border-border bg-secondary/30">*/}
      {/*  <div className="mx-auto container-editorial">*/}
      {/*    <Reveal>*/}
      {/*      <div className="label-eyebrow mb-4">Coverage index</div>*/}
      {/*      <h3 className="font-serif text-3xl md:text-5xl mb-10 tracking-tight">*/}
      {/*        Where our work lives, in numbers.*/}
      {/*      </h3>*/}
      {/*    </Reveal>*/}
      {/*    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">*/}
      {/*      {industries.map((i, idx) => (*/}
      {/*        <Reveal key={i.name} delay={idx * 0.05}>*/}
      {/*          <div className="card-editorial p-6 h-full flex flex-col justify-between min-h-[160px]">*/}
      {/*            <span className="font-mono text-xs text-muted-foreground rule-numeric">{String(idx + 1).padStart(2, '0')}</span>*/}
      {/*            <div>*/}
      {/*              <div className="font-serif text-3xl md:text-4xl leading-none tracking-tight">{i.count}</div>*/}
      {/*              <div className="text-sm mt-2">{i.name}</div>*/}
      {/*            </div>*/}
      {/*          </div>*/}
      {/*        </Reveal>*/}
      {/*      ))}*/}
      {/*    </div>*/}
      {/*    <Reveal className="mt-12">*/}
      {/*      <Link to="/contact" className={buttonVariants({ variant: 'editorial' })}>*/}
      {/*        Talk to us about your category <ArrowUpRight className="w-4 h-4" />*/}
      {/*      </Link>*/}
      {/*    </Reveal>*/}
      {/*  </div>*/}
      {/*</section>*/}
    </motion.main>
  );
}
